import { BreadcrumbProvider } from '@/components/ui/Breadcrumbs';
import { FinishEventModal } from '@/components/ui/FinishEventModal';
import { RepublishEventModal } from '@/components/ui/RepublishEventModal';

import EventOrganizersModal from '@/features/events/components/EventOrganizersModal';
import { useToast } from '@/hooks/useToast';
import { api } from '@/lib/api';
import { getEventDisplayStatus, getEventStatusLabel } from '@/lib/eventHelpers';
import { formatCurrency, formatEventDate } from '@/lib/utils';
import { useAppSelector } from '@/store/hooks';
import type { Event } from '@eventflow/shared-types';
import { CheckCircle2, RefreshCw } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';

interface EventAnalytics {
  event: {
    id: string;
    name: string;
    eventDate: string;
    isPublished: boolean;
    isActive: boolean;
    daysUntilEvent: number;
    hasStarted: boolean;
    status: 'draft' | 'published' | 'finished'; // Estado del ciclo de vida
    event_start_time?: string | null;
    event_end_time?: string | null;
  };
  seats: {
    total: number;
    available: number;
    sold: number;
    locked: number;
    blocked: number;
    occupancyRate: string;
    conversionRate: string;
  };
  revenue: {
    confirmed: number;
    pending: number;
    potential: number;
    mostSoldPrice: number;
  };
  sales: {
    totalPurchases: number;
    confirmedPurchases: number;
    pendingPurchases: number;
    failedPurchases: number;
    totalTicketsSold: number;
    averageTicketsPerPurchase: string;
  };
}

interface CheckInStats {
  totalTickets: number;
  checkedIn: number;
  pending: number;
  percentage: number;
}

// Componente de Skeleton para carga
function StatCardSkeleton() {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 animate-pulse">
      <div className="flex items-center justify-between mb-4">
        <div className="h-4 bg-gray-200 rounded w-24"></div>
        <div className="h-10 w-10 bg-gray-200 rounded-lg"></div>
      </div>
      <div className="h-8 bg-gray-200 rounded w-32 mb-2"></div>
      <div className="h-3 bg-gray-100 rounded w-20"></div>
    </div>
  );
}

// Iconos SVG inline (consistente con el resto del proyecto)
const icons = {
  revenue: (
    <svg
      className="h-6 w-6"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 6v12m-3-2.818l.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  ),
  tickets: (
    <svg
      className="h-6 w-6"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M16.5 6v.75m0 3v.75m0 3v.75m0 3V18m-9-5.25h5.25M7.5 15h3M3.375 5.25c-.621 0-1.125.504-1.125 1.125v3.026a2.999 2.999 0 010 5.198v3.026c0 .621.504 1.125 1.125 1.125h17.25c.621 0 1.125-.504 1.125-1.125v-3.026a2.999 2.999 0 010-5.198V6.375c0-.621-.504-1.125-1.125-1.125H3.375z"
      />
    </svg>
  ),
  checkIn: (
    <svg
      className="h-6 w-6"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  ),
  pending: (
    <svg
      className="h-6 w-6"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  ),
  map: (
    <svg
      className="h-6 w-6"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9 6.75V15m6-6v8.25m.503 3.498l4.875-2.437c.381-.19.622-.58.622-1.006V4.82c0-.836-.88-1.38-1.628-1.006l-3.869 1.934c-.317.159-.69.159-1.006 0L9.503 3.252a1.125 1.125 0 00-1.006 0L3.622 5.689C3.24 5.88 3 6.27 3 6.695V19.18c0 .836.88 1.38 1.628 1.006l3.869-1.934c.317-.159.69-.159 1.006 0l4.994 2.497c.317.158.69.158 1.006 0z"
      />
    </svg>
  ),
  qr: (
    <svg
      className="h-6 w-6"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M3.75 4.875c0-.621.504-1.125 1.125-1.125h4.5c.621 0 1.125.504 1.125 1.125v4.5c0 .621-.504 1.125-1.125 1.125h-4.5A1.125 1.125 0 013.75 9.375v-4.5zM3.75 14.625c0-.621.504-1.125 1.125-1.125h4.5c.621 0 1.125.504 1.125 1.125v4.5c0 .621-.504 1.125-1.125 1.125h-4.5a1.125 1.125 0 01-1.125-1.125v-4.5zM13.5 4.875c0-.621.504-1.125 1.125-1.125h4.5c.621 0 1.125.504 1.125 1.125v4.5c0 .621-.504 1.125-1.125 1.125h-4.5A1.125 1.125 0 0113.5 9.375v-4.5z"
      />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M6.75 6.75h.75v.75h-.75v-.75zM6.75 16.5h.75v.75h-.75v-.75zM16.5 6.75h.75v.75h-.75v-.75zM13.5 13.5h.75v.75h-.75v-.75zM13.5 19.5h.75v.75h-.75v-.75zM19.5 13.5h.75v.75h-.75v-.75zM19.5 19.5h.75v.75h-.75v-.75zM16.5 16.5h.75v.75h-.75v-.75z"
      />
    </svg>
  ),
  users: (
    <svg
      className="h-6 w-6"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z"
      />
    </svg>
  ),
  settings: (
    <svg
      className="h-6 w-6"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.324.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.24-.438.613-.431.992a6.759 6.759 0 010 .255c-.007.378.138.75.43.99l1.005.828c.424.35.534.954.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.57 6.57 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.28c-.09.543-.56.941-1.11.941h-2.594c-.55 0-1.02-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.43-.992a6.932 6.932 0 010-.255c.007-.378-.138-.75-.43-.99l-1.004-.828a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.087.22-.128.332-.183.582-.495.644-.869l.214-1.281z"
      />
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  ),
  analytics: (
    <svg
      className="h-6 w-6"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z"
      />
    </svg>
  ),
};

export default function EventDashboardPage() {
  const { id: eventId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [analytics, setAnalytics] = useState<EventAnalytics | null>(null);
  const [checkInStats, setCheckInStats] = useState<CheckInStats | null>(null);
  const [eventDetails, setEventDetails] = useState<Event | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [finishModalOpen, setFinishModalOpen] = useState(false);
  const [isFinishing, setIsFinishing] = useState(false);

  const [republishModalOpen, setRepublishModalOpen] = useState(false);
  const [isRepublishing, setIsRepublishing] = useState(false);
  const [teamModalOpen, setTeamModalOpen] = useState(false);
  const { success, error: showError } = useToast();
  const { organizer } = useAppSelector((state) => state.auth);

  useEffect(() => {
    const action = searchParams.get('action');
    if (action === 'team') {
      setTeamModalOpen(true);
      // Limpiar query param
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);
        newParams.delete('action');
        return newParams;
      });
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (!eventId) {
      navigate('/dashboard/events');
      return;
    }

    const fetchAnalytics = async () => {
      try {
        setIsLoading(true);
        const data = await api.get<EventAnalytics>(`/events/${eventId}/analytics`);
        setAnalytics(data);

        try {
          const stats = await api.get<CheckInStats>(`/checkin/stats/${eventId}`);
          setCheckInStats(stats);
        } catch (err) {
          console.warn('Error fetching check-in stats:', err);
          setCheckInStats(null);
        }

        try {
          const details = await api.get<Event>(`/events/${eventId}`);
          setEventDetails(details);
        } catch (err) {
          console.warn('Error fetching event details:', err);
          setEventDetails(null);
        }
      } catch (err) {
        console.error('Error fetching analytics:', err);
        setError('No se pudo cargar la información del evento');
      } finally {
        setIsLoading(false);
      }
    };

    fetchAnalytics();
  }, [eventId, navigate]);

  // Finalizar evento manualmente
  const handleFinish = async () => {
    if (!analytics?.event) return;

    try {
      setIsFinishing(true);
      await api.post(`/events/${eventId}/finish`, {});
      // Actualizar el estado local
      setAnalytics((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          event: {
            ...prev.event,
            status: 'finished',
            isPublished: false,
          },
        };
      });
      success('Evento finalizado exitosamente');
      setFinishModalOpen(false);
    } catch (err) {
      console.error('Error finishing event:', err);
      showError(
        'Error al finalizar evento: ' + (err instanceof Error ? err.message : 'Error desconocido')
      );
    } finally {
      setIsFinishing(false);
    }
  };



  // Volver a publicar evento con nuevas fechas
  const handleRepublishEvent = async (data: { event_date: string; event_start_time: string; event_end_time: string }) => {
    if (!analytics?.event) return;

    try {
      setIsRepublishing(true);
      await api.post(`/events/${eventId}/republish`, data);
      // Actualizar el estado local
      setAnalytics((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          event: {
            ...prev.event,
            eventDate: data.event_date,
            status: 'published',
            isPublished: true,
          },
        };
      });
      success('Evento vuelto a publicar exitosamente con nuevas fechas');
      setRepublishModalOpen(false);
    } catch (err) {
      console.error('Error republishing event:', err);
      showError(
        'Error al volver a publicar evento: ' + (err instanceof Error ? err.message : 'Error desconocido')
      );
    } finally {
      setIsRepublishing(false);
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="space-y-8">
        {/* Header skeleton */}
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-64 mb-2"></div>
          <div className="h-4 bg-gray-100 rounded w-48"></div>
        </div>

        {/* Stats skeleton */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatCardSkeleton />
          <StatCardSkeleton />
          <StatCardSkeleton />
          <StatCardSkeleton />
        </div>

        {/* Quick actions skeleton */}
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-40 mb-4"></div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-24 bg-gray-100 rounded-xl"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !analytics) {
    return (
      <div className="text-center py-12">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-100 mb-4">
          <svg
            className="h-8 w-8 text-red-600"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
        </div>
        <p className="text-red-600 mb-4">{error || 'No se pudo cargar el evento'}</p>
        <button onClick={() => navigate('/dashboard/events')} className="btn-primary">
          Volver a Mis Eventos
        </button>
      </div>
    );
  }

  const { event, seats, revenue, sales } = analytics;
  const confirmedTicketsSold = sales.totalTicketsSold;
  const hiddenSeats = Array.isArray(eventDetails?.layout_data?.shapes)
    ? eventDetails!.layout_data!.shapes
      .filter((shape: any) => shape?.hidden === true && shape?.category !== 'overlay')
      .reduce((sum: number, shape: any) => {
        const capacity = typeof shape?.capacity === 'number' ? shape.capacity : 0;
        return sum + (capacity > 0 ? capacity : 1);
      }, 0)
    : 0;
  const visibleTotalSeats = Math.max(seats.total - hiddenSeats, 0);
  const soldPercentage =
    visibleTotalSeats > 0 ? (confirmedTicketsSold / visibleTotalSeats) * 100 : 0;
  const badgeStatus = getEventDisplayStatus({
    status: event.status,
    event_date: event.eventDate,
    event_end_time: event.event_end_time ?? null,
  });
  const checkedInCount = checkInStats?.checkedIn ?? 0;
  const totalTicketsForCheckIn = checkInStats?.totalTickets ?? 0;
  const checkInPercentage =
    totalTicketsForCheckIn > 0 ? (checkedInCount / totalTicketsForCheckIn) * 100 : 0;

  return (
    <BreadcrumbProvider eventTitle={event.name}>
      <div className="space-y-8">
        {/* Header del Evento */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900">{event.name}</h1>
              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${badgeStatus === 'finished'
                  ? 'bg-slate-100 text-slate-800'
                  : badgeStatus === 'published'
                    ? 'bg-green-100 text-green-800'
                    : 'bg-yellow-100 text-yellow-800'
                  }`}
              >
                {getEventStatusLabel(badgeStatus)}
              </span>
            </div>
            <p className="text-gray-600 flex items-center gap-2">
              <svg
                className="h-5 w-5 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
              {formatEventDate(event.eventDate)}
              {badgeStatus === 'finished' ? (
                <span className="text-slate-600 font-medium">• {getEventStatusLabel(badgeStatus)}</span>
              ) : event.hasStarted ? (
                <span className="text-orange-600 font-medium">• En curso</span>
              ) : event.daysUntilEvent > 0 && (
                <span className="text-blue-600 font-medium">• en {event.daysUntilEvent} días</span>
              )}
            </p>
          </div>

          <div className="flex gap-2">
            <Link
              to={`/dashboard/events/${eventId}/edit`}
              className="btn-secondary flex items-center gap-2"
            >
              {icons.settings}
              <span className="hidden sm:inline">Configuración</span>
            </Link>
            {/* Botón Finalizar Evento (solo si NO se muestra como finalizado) */}
            {badgeStatus !== 'finished' && (
              <button
                onClick={() => setFinishModalOpen(true)}
                className="btn-secondary flex items-center gap-2 text-blue-600 border-blue-300 hover:bg-blue-50"
              >
                <CheckCircle2 className="h-5 w-5" />
                <span className="hidden sm:inline">Finalizar Evento</span>
              </button>
            )}

            {/* Botón Volver a Publicar (solo si está finalizado) */}
            {badgeStatus === 'finished' && (
              <button
                onClick={() => setRepublishModalOpen(true)}
                className="btn-secondary flex items-center gap-2 text-purple-600 border-purple-300 hover:bg-purple-50"
              >
                <RefreshCw className="h-5 w-5" />
                <span className="hidden sm:inline">Volver a Publicar</span>
              </button>
            )}
            <Link
              to={`/eventos/${eventId}`}
              target="_blank"
              className="btn-primary flex items-center gap-2"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                />
              </svg>
              <span className="hidden sm:inline">Ver Evento</span>
            </Link>
          </div>
        </div>

        {/* Grid de Métricas */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {/* Ingresos Totales */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-medium text-gray-600">Ingresos Confirmados</h3>
              <div className="p-2 bg-green-100 rounded-lg text-green-600">{icons.revenue}</div>
            </div>
            <p className="text-3xl font-bold text-gray-900">{formatCurrency(revenue.confirmed)}</p>
            {revenue.pending > 0 && (
              <p className="text-sm text-yellow-600 mt-1">
                + {formatCurrency(revenue.pending)} pendientes
              </p>
            )}
          </div>

          {/* Boletos Vendidos */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-medium text-gray-600">Boletos Vendidos</h3>
              <div className="p-2 bg-blue-100 rounded-lg text-blue-600">{icons.tickets}</div>
            </div>
            <p className="text-3xl font-bold text-gray-900">
              {confirmedTicketsSold}{' '}
              <span className="text-lg font-normal text-gray-500">/ {visibleTotalSeats}</span>
            </p>
            {/* Barra de progreso */}
            <div className="mt-3">
              <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-blue-500 rounded-full transition-all duration-500"
                  style={{ width: `${soldPercentage}%` }}
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">{soldPercentage.toFixed(1)}% vendido</p>
            </div>
          </div>

          {/* Asistencia (Check-ins) */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-medium text-gray-600">Check-ins</h3>
              <div className="p-2 bg-purple-100 rounded-lg text-purple-600">{icons.checkIn}</div>
            </div>
            <p className="text-3xl font-bold text-gray-900">
              {checkedInCount}{' '}
              <span className="text-lg font-normal text-gray-500">/ {totalTicketsForCheckIn}</span>
            </p>
            <div className="mt-3">
              <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-purple-500 rounded-full transition-all duration-500"
                  style={{ width: `${Math.min(checkInPercentage, 100)}%` }}
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">{checkInPercentage.toFixed(1)}% registrados</p>
            </div>
          </div>

          {/* Pagos Pendientes */}
          <div
            className={`bg-white rounded-xl shadow-sm border p-6 hover:shadow-md transition-shadow ${sales.pendingPurchases > 0 ? 'border-red-200 bg-red-50/30' : 'border-gray-200'
              }`}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-medium text-gray-600">Pagos Pendientes</h3>
              <div
                className={`p-2 rounded-lg ${sales.pendingPurchases > 0
                  ? 'bg-red-100 text-red-600'
                  : 'bg-gray-100 text-gray-600'
                  }`}
              >
                {icons.pending}
              </div>
            </div>
            <p
              className={`text-3xl font-bold ${sales.pendingPurchases > 0 ? 'text-red-600' : 'text-gray-900'
                }`}
            >
              {sales.pendingPurchases}
            </p>
            {sales.pendingPurchases > 0 && (
              <Link
                to={`/dashboard/events/${eventId}/payments`}
                className="text-sm text-red-600 hover:text-red-700 font-medium mt-1 inline-flex items-center gap-1"
              >
                Ver pendientes →
              </Link>
            )}
          </div>
        </div>

        {/* Accesos Rápidos */}
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Accesos Rápidos</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
            <Link
              to={`/dashboard/events/${eventId}/map`}
              className="group bg-white rounded-xl border border-gray-200 p-5 hover:border-blue-300 hover:shadow-md transition-all flex flex-col items-center text-center"
            >
              <div className="p-3 bg-blue-50 rounded-xl text-blue-600 group-hover:bg-blue-100 transition-colors mb-3">
                {icons.map}
              </div>
              <span className="font-medium text-gray-900 group-hover:text-blue-600 transition-colors">
                Editar Mapa
              </span>
              <span className="text-xs text-gray-500 mt-1">Asientos y precios</span>
            </Link>

            <Link
              to={`/dashboard/events/${eventId}/checkin`}
              className="group bg-white rounded-xl border border-gray-200 p-5 hover:border-green-300 hover:shadow-md transition-all flex flex-col items-center text-center"
            >
              <div className="p-3 bg-green-50 rounded-xl text-green-600 group-hover:bg-green-100 transition-colors mb-3">
                {icons.qr}
              </div>
              <span className="font-medium text-gray-900 group-hover:text-green-600 transition-colors">
                Escanear QRs
              </span>
              <span className="text-xs text-gray-500 mt-1">Check-in en vivo</span>
            </Link>

            <Link
              to={`/dashboard/events/${eventId}/attendees`}
              className="group bg-white rounded-xl border border-gray-200 p-5 hover:border-purple-300 hover:shadow-md transition-all flex flex-col items-center text-center"
            >
              <div className="p-3 bg-purple-50 rounded-xl text-purple-600 group-hover:bg-purple-100 transition-colors mb-3">
                {icons.users}
              </div>
              <span className="font-medium text-gray-900 group-hover:text-purple-600 transition-colors">
                Ver Asistentes
              </span>
              <span className="text-xs text-gray-500 mt-1">Lista completa</span>
            </Link>

            <button
              onClick={() => setTeamModalOpen(true)}
              className="group bg-white rounded-xl border border-gray-200 p-5 hover:border-teal-300 hover:shadow-md transition-all flex flex-col items-center text-center w-full"
            >
              <div className="p-3 bg-teal-50 rounded-xl text-teal-600 group-hover:bg-teal-100 transition-colors mb-3">
                {icons.users}
              </div>
              <span className="font-medium text-gray-900 group-hover:text-teal-600 transition-colors">
                Gestionar Equipo
              </span>
              <span className="text-xs text-gray-500 mt-1">Colaboradores</span>
            </button>

            <Link
              to={`/dashboard/events/${eventId}/payments`}
              className="group bg-white rounded-xl border border-gray-200 p-5 hover:border-orange-300 hover:shadow-md transition-all flex flex-col items-center text-center relative"
            >
              {sales.pendingPurchases > 0 && (
                <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full">
                  {sales.pendingPurchases}
                </span>
              )}
              <div className="p-3 bg-orange-50 rounded-xl text-orange-600 group-hover:bg-orange-100 transition-colors mb-3">
                {icons.pending}
              </div>
              <span className="font-medium text-gray-900 group-hover:text-orange-600 transition-colors">
                Pagos
              </span>
              <span className="text-xs text-gray-500 mt-1">Confirmar pagos</span>
            </Link>

            <Link
              to={`/dashboard/events/${eventId}/analytics`}
              className="group bg-white rounded-xl border border-gray-200 p-5 hover:border-indigo-300 hover:shadow-md transition-all flex flex-col items-center text-center"
            >
              <div className="p-3 bg-indigo-50 rounded-xl text-indigo-600 group-hover:bg-indigo-100 transition-colors mb-3">
                {icons.analytics}
              </div>
              <span className="font-medium text-gray-900 group-hover:text-indigo-600 transition-colors">
                Analíticas
              </span>
              <span className="text-xs text-gray-500 mt-1">Estadísticas</span>
            </Link>
          </div>
        </div>

        {/* Resumen Rápido */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Estado de Asientos */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="font-semibold text-gray-900 mb-4">Distribución de Asientos</h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-green-500"></div>
                  <span className="text-sm text-gray-600">Disponibles</span>
                </div>
                <span className="font-medium">{seats.available}</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-gray-500"></div>
                  <span className="text-sm text-gray-600">Vendidos</span>
                </div>
                <span className="font-medium">{confirmedTicketsSold}</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                  <span className="text-sm text-gray-600">Reservados</span>
                </div>
                <span className="font-medium">{seats.locked}</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-red-500"></div>
                  <span className="text-sm text-gray-600">Bloqueados</span>
                </div>
                <span className="font-medium">{seats.blocked}</span>
              </div>
            </div>
          </div>

          {/* Resumen Financiero */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="font-semibold text-gray-900 mb-4">Resumen Financiero</h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Ingresos confirmados</span>
                <span className="font-medium text-green-600">
                  {formatCurrency(revenue.confirmed)}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Ingresos pendientes</span>
                <span className="font-medium text-yellow-600">
                  {formatCurrency(revenue.pending)}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Potencial restante</span>
                <span className="font-medium text-gray-500">
                  {formatCurrency(revenue.potential)}
                </span>
              </div>
              <div className="border-t pt-3 mt-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-900">Compras totales</span>
                  <span className="font-bold text-gray-900">{sales.totalPurchases}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal de finalización */}
      <FinishEventModal
        isOpen={finishModalOpen}
        onClose={() => setFinishModalOpen(false)}
        onConfirm={handleFinish}
        eventName={analytics?.event.name || ''}
        eventDate={analytics ? formatEventDate(analytics.event.eventDate) : ''}
        isFinishing={isFinishing}
      />



      {/* Modal de republicar */}
      <RepublishEventModal
        isOpen={republishModalOpen}
        onClose={() => setRepublishModalOpen(false)}
        onConfirm={handleRepublishEvent}
        eventName={analytics?.event.name || ''}
        currentDate={analytics ? formatEventDate(analytics.event.eventDate) : ''}
        currentStartTime={analytics?.event.event_start_time || ''}
        currentEndTime={analytics?.event.event_end_time || ''}
        isRepublishing={isRepublishing}
      />

      {/* Modal de Equipo */}
      {teamModalOpen && eventDetails && (
        <EventOrganizersModal
          eventId={eventId!}
          eventName={eventDetails.name}
          isCreator={eventDetails.organizer_id === organizer?.id}
          readOnly={badgeStatus === 'finished' || (analytics?.event.status as string) === 'archived'}
          onClose={() => setTeamModalOpen(false)}
          onUpdate={() => {
            // Recargar datos si es necesario
          }}
        />
      )}
    </BreadcrumbProvider>
  );
}
